import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import facebookIcon from '@/assets/Icons/ic_facebook.svg';
import instagramIcon from '@/assets/Icons/ic_instagram.svg';

import { ContextProvider } from '@/context/ProjectContext.jsx';

import { dayjs } from '@/utils/dayjsSetup.js';
import { isEternoDomain } from '@/utils/helpers.js';

import { useConfigStore, usePersistedPrismicStore } from '@/store.js';

import LanguageSelector from './LanguageSelector.jsx';

const useStyles = makeStyles((theme) => {
    // Shared building blocks so links, muted text and section widths stay consistent.
    const sectionWidth = {
        maxWidth: theme.layout.contentWidth,
        marginLeft: 'auto',
        marginRight: 'auto',
    };

    const footerLink = {
        textDecoration: 'none',
        color: theme.palette.common.darkGrey,
        marginBottom: 18,
        fontSize: 15,
        transition: 'opacity .2s',
        '&:hover': { opacity: 0.7 },
        '&:last-child': { marginBottom: 0 },
    };

    const mutedSmallText = {
        fontSize: 13,
        lineHeight: 1.7,
    };

    return {
        footer: {
            borderTop: theme.border.light,
            padding: '40px 0',
            marginTop: theme.spacing(8),
            backgroundColor: theme.palette.common.lightBrownDropdown,
            width: '100%',
            [theme.breakpoints.down('xs')]: {
                padding: '30px 0',
            },
        },
        footerTop: {
            ...sectionWidth,
            display: 'grid',
            gridTemplateColumns: '1.2fr 1fr 0.8fr',
            gap: 80,
            padding: '0 32px',
            [theme.breakpoints.down('xs')]: {
                gridTemplateColumns: '1fr',
                gap: 24,
            },
        },
        footerColumn: {
            display: 'flex',
            flexDirection: 'column',
            [theme.breakpoints.down('xs')]: {
                width: '100%',
                '&:not(:first-child)': {
                    borderTop: theme.border.light,
                    paddingTop: 24,
                },
            },
        },
        languageWrapper: {
            alignSelf: 'flex-start',
            marginBottom: 32,
        },
        copyright: {
            display: 'flex',
            flexDirection: 'column',
        },
        copyrightText: {
            margin: '4px 0',
            fontSize: 15,
            textTransform: 'none',
        },
        link: footerLink,
        socialLink: {
            ...footerLink,
            display: 'flex',
            alignItems: 'center',
            gap: 10,
        },
        socialName: {
            textTransform: 'capitalize',
        },
        socialIcon: {
            height: 18,
            width: 18,
        },
        footerBottom: {
            ...sectionWidth,
            marginTop: 48,
            padding: '24px 32px 0',
            borderTop: theme.border.light,
            [theme.breakpoints.down('xs')]: {
                marginTop: 32,
            },
        },
        registrationNote: {
            maxWidth: 900,
            marginBottom: 16,
        },
        registrationNoteTitle: {
            color: theme.palette.common.red,
            fontSize: 13,
        },
        registrationNoteText: mutedSmallText,
        legalText: {
            ...mutedSmallText,
            '& a': {
                color: 'inherit',
                textDecoration: 'underline',
            },
        },
    };
});

const SocialPlatform = {
    INSTAGRAM: instagramIcon,
    FACEBOOK: facebookIcon,
};

const Footer = () => {
    const classes = useStyles();
    const { i18n, t } = useTranslation();
    const { setLanguageGlobal } = useContext(ContextProvider);
    const { [i18n.language]: footerPrismicData } = usePersistedPrismicStore((state) => state.footerData);
    const currentCustomer = useConfigStore((state) => state.currentCustomer);

    useEffect(() => {
        const lang = localStorage.getItem('languageGlobal');
        setLanguageGlobal(lang || 'de');
    }, []);

    if (!footerPrismicData.dataSet) return null;

    const showRegistrationHint = currentCustomer?.is_registration_hint_shown;
    const showLegal = !isEternoDomain();
    const [legalTextBefore, legalTextAfter] = showLegal
        ? footerPrismicData.content.legal_text[0].text.split('{link_placeholder}')
        : [];

    const socialLinks = currentCustomer
        ? currentCustomer.social_media_links.map((socialLink) => ({
              url: socialLink.link.url,
              icon: SocialPlatform[socialLink.platform],
              name: socialLink.platform.toLowerCase(),
          }))
        : [
              { url: footerPrismicData.content.fb_url.url, icon: facebookIcon, name: 'Facebook' },
              { url: footerPrismicData.content.instagram_url.url, icon: instagramIcon, name: 'Instagram' },
          ];

    return (
        <footer className={classes.footer}>
            <div className={classes.footerTop}>
                {/* Column 1 – language switcher and copyright */}
                <div className={classes.footerColumn}>
                    <div className={classes.languageWrapper}>
                        <LanguageSelector />
                    </div>
                    <div className={classes.copyright}>
                        <Typography className={classes.copyrightText}>
                            {`Copyright © ${dayjs().format('YYYY')}`}
                        </Typography>
                        <Typography className={classes.copyrightText}>Eterno GmbH</Typography>
                    </div>
                </div>

                {/* Column 2 – navigation links */}
                <div className={classes.footerColumn}>
                    {currentCustomer.footer_links.map((footerLink) => (
                        <a
                            key={footerLink.link.url}
                            className={classes.link}
                            target="_self"
                            rel="noopener noreferrer"
                            href={footerLink.link.url}
                        >
                            {footerLink.title[0].text}
                        </a>
                    ))}
                </div>

                {/* Column 3 – social media icons and names */}
                <div className={classes.footerColumn}>
                    {socialLinks.map((social) => (
                        <a key={social.url} className={classes.socialLink} href={social.url} target="_self">
                            <img className={classes.socialIcon} src={social.icon ?? ''} alt={`${social.name} link`} />
                            <span className={classes.socialName}>{social.name}</span>
                        </a>
                    ))}
                </div>
            </div>

            {(showRegistrationHint || showLegal) && (
                <div className={classes.footerBottom}>
                    {showRegistrationHint && (
                        <div className={classes.registrationNote}>
                            <Typography className={classes.registrationNoteTitle}>
                                {t('RegistrationNoteTitle')}:
                            </Typography>
                            <Typography className={classes.registrationNoteText}>
                                {t('RegistrationNoteText')}
                            </Typography>
                        </div>
                    )}
                    {showLegal && (
                        <Typography component="p" className={classes.legalText}>
                            <span>{legalTextBefore}</span>
                            <a href={footerPrismicData.content.legal_link.url} target="_blank" rel="noreferrer">
                                {footerPrismicData.content.legal_link_text[0].text}
                            </a>
                            <span>{legalTextAfter}</span>
                        </Typography>
                    )}
                </div>
            )}
        </footer>
    );
};

export default Footer;
